import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaSort, FaSortUp, FaSortDown, FaSpinner } from "react-icons/fa";
import api from "../../../services/api";

// Column group colors (border-top accent)
const GROUP_COLORS = {
  meta: "#6b7280",     // Gray — metadata
  pre: "#8b5cf6",      // Purple — pre-treatment
  behavioral: "#f59e0b", // Orange — interaction behavior
  post: "#22c55e",     // Green — post-treatment DVs
};

const GROUP_BG = {
  meta: "bg-gray-700/30",
  pre: "bg-purple-900/10",
  behavioral: "bg-orange-900/10",
  post: "bg-green-900/10",
};

const DatasetTable = ({ studyId, conditions }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const adminToken = localStorage.getItem("admin_token");

  const fetchData = useCallback(async () => {
    if (!adminToken || !studyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/studies/${studyId}/export`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, studyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build columns from data
  const { columns, rows } = useMemo(() => {
    if (!data?.participants?.length) return { columns: [], rows: [] };

    const participants = data.participants;

    // Discover all response keys
    const preKeys = new Set();
    const postKeys = new Set();
    for (const p of participants) {
      if (p.preSurveyResponses) Object.keys(p.preSurveyResponses).forEach((k) => preKeys.add(k));
      if (p.postSurveyResponses) Object.keys(p.postSurveyResponses).forEach((k) => postKeys.add(k));
    }

    const sortedPreKeys = [...preKeys].sort();
    const sortedPostKeys = [...postKeys].sort();

    const cols = [
      // Metadata
      { key: "participantId", label: "Participant", group: "meta", width: 120 },
      { key: "conditionLabel", label: "Condition", group: "meta", width: 140 },
      { key: "completedAt", label: "Completed", group: "meta", width: 90, format: "date" },
      { key: "preSurveyDurationMs", label: "Pre (s)", group: "meta", width: 60, format: "duration" },
      { key: "postSurveyDurationMs", label: "Post (s)", group: "meta", width: 60, format: "duration" },

      // Pre-survey
      ...sortedPreKeys.map((k) => ({
        key: `pre_${k}`,
        label: k,
        group: "pre",
        width: 70,
        format: isNumericKey(k) ? "likert" : "text",
      })),

      // Behavioral — comprehensive interaction logging
      { key: "behav_messagesPosted", label: "Msgs", group: "behavioral", width: 50 },
      { key: "behav_commentsPosted", label: "Cmts", group: "behavioral", width: 50 },
      { key: "behav_likesGiven", label: "Likes", group: "behavioral", width: 50 },
      { key: "behav_followsGiven", label: "Follows", group: "behavioral", width: 55 },
      { key: "behav_commentLikes", label: "CmtLikes", group: "behavioral", width: 60 },
      { key: "behav_expandComments", label: "Threads", group: "behavioral", width: 55 },
      { key: "behav_totalInteractions", label: "Intxns", group: "behavioral", width: 55 },
      { key: "behav_timeOnPlatform", label: "Time (m)", group: "behavioral", width: 60, format: "minutes" },
      { key: "behav_feedViews", label: "Feeds", group: "behavioral", width: 50 },
      { key: "behav_uniqueMessagesViewed", label: "Viewed", group: "behavioral", width: 55 },
      { key: "behav_totalDwellSec", label: "Dwell (s)", group: "behavioral", width: 60 },
      { key: "behav_avgNotifResponseSec", label: "Notif (s)", group: "behavioral", width: 60 },

      // Post-survey
      ...sortedPostKeys.map((k) => ({
        key: `post_${k}`,
        label: k,
        group: "post",
        width: 70,
        format: isNumericKey(k) ? "likert" : "text",
      })),
    ];

    const tableRows = participants.map((p) => {
      const row = {};
      row.participantId = p.participantId;
      row.conditionId = p.conditionId;
      row.conditionLabel = p.conditionLabel;
      row.completedAt = p.completedAt;
      row.preSurveyDurationMs = p.preSurveyDurationMs;
      row.postSurveyDurationMs = p.postSurveyDurationMs;

      for (const k of sortedPreKeys) {
        row[`pre_${k}`] = p.preSurveyResponses?.[k] ?? null;
      }

      const bd = p.behavioralData;
      const ipt = bd?.interactionsPerType || {};
      row.behav_messagesPosted = bd?.messagesPosted ?? null;
      row.behav_commentsPosted = bd?.commentsPosted ?? null;
      row.behav_likesGiven = (ipt.like || 0) || null;
      row.behav_followsGiven = (ipt.follow || 0) || null;
      row.behav_commentLikes = (ipt.comment_like || 0) || null;
      row.behav_expandComments = (ipt.expand_comments || 0) || null;
      row.behav_totalInteractions = bd?.totalInteractions ?? null;
      row.behav_timeOnPlatform = bd?.timeOnPlatform ?? null;
      row.behav_feedViews = bd?.feedImpressionsSummary?.total ?? null;

      // Dwell time from raw message impressions
      const dwellMs = (bd?.rawMessageImpressions || []).reduce((sum, mi) =>
        sum + (mi.impressions || []).reduce((s, imp) => s + (imp.dwellTimeMs || 0), 0), 0
      );
      const uniqueViewed = new Set();
      (bd?.rawMessageImpressions || []).forEach(mi =>
        (mi.impressions || []).forEach(imp => { if (imp.messageId) uniqueViewed.add(imp.messageId); })
      );
      row.behav_uniqueMessagesViewed = uniqueViewed.size || null;
      row.behav_totalDwellSec = dwellMs > 0 ? Math.round(dwellMs / 1000) : null;

      // Avg notification response time
      const notifs = bd?.notifications || [];
      const responseTimes = notifs.filter(n => n.responseTimeMs != null).map(n => n.responseTimeMs);
      row.behav_avgNotifResponseSec = responseTimes.length > 0
        ? Math.round(responseTimes.reduce((s, t) => s + t, 0) / responseTimes.length / 1000)
        : null;

      for (const k of sortedPostKeys) {
        row[`post_${k}`] = p.postSurveyResponses?.[k] ?? null;
      }

      return row;
    });

    return { columns: cols, rows: tableRows };
  }, [data]);

  // Sorting
  const sortedRows = useMemo(() => {
    if (!sortCol || rows.length === 0) return rows;
    return [...rows].sort((a, b) => {
      let va = a[sortCol] ?? "";
      let vb = b[sortCol] ?? "";
      // Numeric comparison
      const na = Number(va);
      const nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) {
        return sortDir === "asc" ? na - nb : nb - na;
      }
      // String comparison
      va = String(va);
      vb = String(vb);
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [rows, sortCol, sortDir]);

  const handleSort = (colKey) => {
    if (sortCol === colKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(colKey);
      setSortDir("asc");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FaSpinner className="animate-spin text-blue-400 w-6 h-6" />
        <span className="text-gray-400 ml-2">Loading dataset...</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-400 text-sm p-4">Error: {error}</div>;
  }

  if (rows.length === 0) {
    return <p className="text-gray-500 text-sm p-4">No participant data available</p>;
  }

  // Find condition color map
  const condColorMap = {};
  (conditions || []).forEach((c, i) => {
    condColorMap[c.conditionId] = COND_ROW_COLORS[i % COND_ROW_COLORS.length];
  });

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <span className="text-gray-400 text-xs">
          {rows.length} participants × {columns.length} variables
        </span>
        <div className="flex gap-3 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: GROUP_COLORS.meta }}></span>
            Metadata
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: GROUP_COLORS.pre }}></span>
            Pre-treatment
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: GROUP_COLORS.behavioral }}></span>
            Behavioral
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: GROUP_COLORS.post }}></span>
            Post-treatment
          </span>
        </div>
      </div>

      <div className="overflow-auto max-h-[600px]">
        <table className="text-xs border-collapse w-max min-w-full">
          <thead className="sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-2 py-2 text-left font-medium text-gray-300 cursor-pointer select-none whitespace-nowrap border-t-2 ${GROUP_BG[col.group] || ""}`}
                  style={{
                    borderTopColor: GROUP_COLORS[col.group],
                    minWidth: col.width,
                    maxWidth: col.width + 30,
                    backgroundColor: "rgb(31, 41, 55)", // bg-gray-800 solid for sticky
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    <span className="truncate">{col.label}</span>
                    {sortCol === col.key ? (
                      sortDir === "asc" ? <FaSortUp className="w-2 h-2 text-blue-400" /> : <FaSortDown className="w-2 h-2 text-blue-400" />
                    ) : (
                      <FaSort className="w-2 h-2 text-gray-600" />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="border-b border-gray-700/30 hover:bg-gray-700/20"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-2 py-1.5 whitespace-nowrap"
                    style={{ minWidth: col.width, maxWidth: col.width + 30 }}
                  >
                    <CellValue
                      value={row[col.key]}
                      format={col.format}
                      condColor={col.key === "conditionLabel" ? condColorMap[row.conditionId] : null}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Subtle condition row colors
const COND_ROW_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
];

const CellValue = ({ value, format, condColor }) => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-600">—</span>;
  }

  // Condition label with color dot
  if (condColor) {
    return (
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: condColor }}></span>
        <span className="text-gray-300 truncate">{String(value)}</span>
      </span>
    );
  }

  if (format === "date") {
    try {
      return <span className="text-gray-400">{new Date(value).toLocaleDateString()}</span>;
    } catch {
      return <span className="text-gray-400">{String(value)}</span>;
    }
  }

  if (format === "duration") {
    const seconds = Math.round(Number(value) / 1000);
    return <span className="text-gray-400">{seconds > 0 ? seconds : "—"}</span>;
  }

  if (format === "minutes") {
    const minutes = Math.round(Number(value) / 60000);
    return <span className="text-gray-400">{minutes > 0 ? minutes : "—"}</span>;
  }

  if (format === "likert") {
    const num = Number(value);
    if (!isNaN(num)) {
      return (
        <span
          className="text-white font-mono"
          style={{ backgroundColor: likertColor(num), padding: "1px 4px", borderRadius: 3 }}
        >
          {num}
        </span>
      );
    }
  }

  return <span className="text-gray-300 truncate block" style={{ maxWidth: 100 }}>{String(value)}</span>;
};

/**
 * Subtle background gradient for Likert values (1-7 or 0-10).
 * Low values are reddish, mid are neutral, high are blueish.
 */
function likertColor(val) {
  // Normalize to 0-1 assuming 1-7 or 0-10 range
  const norm = val <= 10 ? val / 10 : val / 100;
  const clamped = Math.max(0, Math.min(1, norm));
  // Interpolate from muted red -> neutral gray -> muted blue
  if (clamped < 0.5) {
    const t = clamped / 0.5;
    const r = Math.round(120 + t * (55 - 120));
    const g = Math.round(40 + t * (55 - 40));
    const b = Math.round(40 + t * (75 - 40));
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  } else {
    const t = (clamped - 0.5) / 0.5;
    const r = Math.round(55 + t * (40 - 55));
    const g = Math.round(55 + t * (60 - 55));
    const b = Math.round(75 + t * (130 - 75));
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  }
}

/**
 * Heuristic: keys ending in digits (e.g., "nfc_1", "att_pre_2") or
 * known slider/button_scale question IDs are likely numeric.
 */
function isNumericKey(key) {
  return /(_\d+$|\d$)/.test(key) ||
    /^(trust_|efficacy|therm_|social_distance|delib|global_stance|att_certainty)/.test(key);
}

export default DatasetTable;
